import get from "lodash/get";

import { createSlice, EntityId, PayloadAction, Update } from "@reduxjs/toolkit";
import { v4 } from "uuid";

import { api } from "src/store/api";
import {
  abortHandler,
  carFinishHandler,
  carProgressHandler,
  endTechBreakHandler,
  finishHandler,
  initHandler,
  raceFinishHandler,
  raceStartHandler,
  startExpectedHandler,
  startHandler,
  techBreakHandler,
  winHandler,
} from "./utils";
import { Bet, Car, Market } from "src/types";
import { betsAdapter, carsAdapter, gamesAdapter } from "./adapters";
import { betsSelectors } from "./selectors";
import { DEFAULT_LIMITS } from "./constants";
import { socketActions } from "src/store/actions";
import { State, Undo } from "./types";

const STATE: State = {
  bets: betsAdapter.getInitialState({}),
  cars: carsAdapter.getInitialState({}),
  gameId: 0,
  games: gamesAdapter.getInitialState(),
  kind: "none",
  limit: { bet: null, uuid: "" },
  limits: DEFAULT_LIMITS,
  loading: false,
  betCreateBatchPending: 0,
  market: "result",
  prev: betsAdapter.getInitialState({}),
  step: 1,
  undo: [],
};

export const coreSlice = createSlice({
  name: "core",
  initialState: STATE,
  reducers: {
    betUpdated: (state, action: PayloadAction<Update<Bet>>) => {
      betsAdapter.updateOne(state.bets, action.payload);
    },
    betsUpdated: (state, action: PayloadAction<Array<Update<Bet>>>) => {
      const limits = state.limits;

      const updates: Array<Update<Bet>> = [];

      const undos: Array<Undo> = [];

      action.payload.forEach((update) => {
        const bet = betsSelectors.selectById(state, update.id);
        const limit = get(limits, bet?.type || "");

        if (bet && limit) {
          if (limit.max < (update.changes.amount || 0)) {
            update.changes.amount = limit.max;

            state.limit.bet = { uuid: update.id };
            state.limit.uuid = v4();
          }

          const updated = bet.amount !== update.changes.amount;

          if (limit.min <= (update.changes.amount || 0) && updated) {
            updates.push(update);

            const undo: Undo = {
              amount: bet.amount,
              status: bet.status,
              uuid: bet.uuid,
            };

            undos.push(undo);
          }
        }
      });

      if (undos.length) {
        state.undo.push(undos);
      }

      betsAdapter.updateMany(state.bets, updates);
    },
    betsRemoved: (state, action: PayloadAction<Array<EntityId>>) => {
      betsAdapter.removeMany(state.bets, action.payload);
    },
    betsUndo: (state) => {
      const updates: Array<Update<Bet>> = [];

      const undos = state.undo.pop();

      undos?.forEach((undo) => {
        const update: Update<Bet> = { id: undo.uuid, changes: {} };
        update.changes.amount = undo.amount;
        update.changes.status = undo.status;
        updates.push(update);
      });

      betsAdapter.updateMany(state.bets, updates);
    },
    carUpdated: (state, action: PayloadAction<Update<Car>>) => {
      carsAdapter.updateOne(state.cars, action.payload);
    },
    carsRemoved: (state, action: PayloadAction<Array<EntityId>>) => {
      carsAdapter.removeMany(state.cars, action.payload);
    },
    gamesRemoved: (state, action: PayloadAction<Array<EntityId>>) => {
      gamesAdapter.removeMany(state.games, action.payload);
    },
    limitRemoved: (state) => {
      state.limit.bet = null;
      state.limit.uuid = "";
    },
    marketUpdated: (state, action: PayloadAction<Market>) => {
      state.limit.bet = null;
      state.limit.uuid = "";
      state.market = action.payload;
    },
    betCreateBatchStarted: (state, action: PayloadAction<number>) => {
      state.betCreateBatchPending = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(socketActions.messageInit, initHandler)
      .addCase(socketActions.messageStart, startHandler)
      .addCase(socketActions.messageRaceStart, raceStartHandler)
      .addCase(socketActions.messageCarProgress, carProgressHandler)
      .addCase(socketActions.messageCarFinish, carFinishHandler)
      .addCase(socketActions.messageRaceFinish, raceFinishHandler)
      .addCase(socketActions.messageFinish, finishHandler)
      .addCase(socketActions.messageWin, winHandler)
      .addCase(socketActions.messageStartExpected, startExpectedHandler)
      .addCase(socketActions.messageAbort, abortHandler)
      .addCase(socketActions.messageTechBreak, techBreakHandler)
      .addCase(socketActions.messageEndTechBreak, endTechBreakHandler)
      .addCase(socketActions.close, () => STATE)
      .addMatcher(api.endpoints.betCreate.matchPending, (state, action) => {
        const requestBets = action.meta.arg.originalArgs.bets || [];
        const isBatch = state.betCreateBatchPending > 0;

        const betsToPending = isBatch
          ? requestBets
          : betsSelectors
              .selectAll(state)
              .filter((bet) => bet.status === "selected");

        betsToPending.forEach((bet) => {
          const update: Update<Bet> = { id: bet.uuid, changes: {} };
          update.changes.status = "pending";
          betsAdapter.updateOne(state.bets, update);
        });

        state.limit.bet = null;
        state.limit.uuid = "";
        state.loading = true;
      })
      .addMatcher(api.endpoints.betCreate.matchFulfilled, (state, action) => {
        const bets = action.meta.arg.originalArgs.bets || [];

        const playId = state.gameId;
        const nextId = playId + 1;

        if (action.payload.result) {
          const ids = action.payload.result.bet_ids;

          bets.forEach((bet, index) => {
            const update: Update<Bet> = { id: bet.uuid, changes: {} };
            update.changes.id = ids[index] || 0;
            update.changes.status = "confirmed";
            betsAdapter.updateOne(state.bets, update);
          });
        }

        if (action.payload.error) {
          bets.forEach((bet) => {
            const update: Update<Bet> = { id: bet.uuid, changes: {} };
            update.changes.status = bet.gameId === nextId ? "selected" : "none";
            betsAdapter.updateOne(state.bets, update);
          });
        }

        if (state.betCreateBatchPending > 0) {
          state.betCreateBatchPending -= 1;
          if (state.betCreateBatchPending === 0) {
            state.loading = false;
          }
        } else {
          state.loading = false;
        }
      })
      .addMatcher(api.endpoints.betCreate.matchRejected, (state, action) => {
        const bets = action.meta.arg.originalArgs.bets || [];

        const playId = state.gameId;
        const nextId = playId + 1;

        bets.forEach((bet) => {
          const update: Update<Bet> = { id: bet.uuid, changes: {} };
          update.changes.status = bet.gameId === nextId ? "selected" : "none";
          betsAdapter.updateOne(state.bets, update);
        });

        if (state.betCreateBatchPending > 0) {
          state.betCreateBatchPending -= 1;
          if (state.betCreateBatchPending === 0) {
            state.loading = false;
          }
        } else {
          state.loading = false;
        }
      });
  },
});

export const coreActions = {
  ...coreSlice.actions,
};
